import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import type { JourneyStep } from '../../types/premium';
import ScrollReveal from './ScrollReveal';
import './SolutionJourney.css';

interface SolutionJourneyProps {
  title: string;
  steps: JourneyStep[];
}

const stepViewport = { once: true, amount: 0.35 as const };

const SolutionJourney: React.FC<SolutionJourneyProps> = ({ title, steps }) => {
  const containerRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="premium-journey" ref={containerRef}>
      <div className="premium-journey__inner">
        <ScrollReveal className="premium-journey__header">
          <span className="premium-journey__eyebrow">Chapter II — Your journey</span>
          <h2 className="premium-journey__title">{title}</h2>
        </ScrollReveal>

        <div className={`premium-journey__timeline ${prefersReducedMotion ? 'premium-journey__timeline--reduce-motion' : ''}`}>
          <motion.div className="premium-journey__line" style={{ scaleY: lineScale }} aria-hidden="true" />

          {steps.map((step, index) => {
            const stagger = index * 0.12;
            const numMotion = prefersReducedMotion
              ? {
                  initial: { opacity: 0 },
                  whileInView: { opacity: 1 },
                  transition: { delay: stagger, duration: 0.4 },
                }
              : {
                  initial: { opacity: 0, x: -32, scale: 0.85 },
                  whileInView: { opacity: 1, x: 0, scale: 1 },
                  transition: { delay: stagger, duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
                };

            const cardMotion = prefersReducedMotion
              ? {
                  initial: { opacity: 0 },
                  whileInView: { opacity: 1 },
                  transition: { delay: stagger + 0.1, duration: 0.45 },
                }
              : {
                  initial: { opacity: 0, y: 40, scale: 0.97 },
                  whileInView: { opacity: 1, y: 0, scale: 1 },
                  transition: {
                    delay: stagger + 0.1,
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1] as const,
                  },
                };

            return (
              <div key={step.title} className="premium-journey__step">
                <motion.div
                  className="premium-journey__num"
                  viewport={stepViewport}
                  initial={numMotion.initial}
                  whileInView={numMotion.whileInView}
                  transition={numMotion.transition}
                >
                  {index + 1}
                </motion.div>

                <motion.article
                  className="premium-journey__card"
                  viewport={stepViewport}
                  initial={cardMotion.initial}
                  whileInView={cardMotion.whileInView}
                  transition={cardMotion.transition}
                  whileHover={prefersReducedMotion ? undefined : { y: -2 }}
                >
                  <h3 className="premium-journey__card-title">{step.title}</h3>
                  {step.paragraphs.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                  {step.bullets && step.bullets.length > 0 ? (
                    <ul className="premium-journey__list">
                      {step.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {step.highlight ? (
                    <div className="premium-journey__highlight">
                      {step.highlight.paragraphs?.map((p) => (
                        <p key={p}>{p}</p>
                      ))}
                      {step.highlight.bullets && step.highlight.bullets.length > 0 ? (
                        <ul className="premium-journey__list">
                          {step.highlight.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                      {step.highlight.conclusion ? (
                        <p className="premium-journey__conclusion">{step.highlight.conclusion}</p>
                      ) : null}
                    </div>
                  ) : null}
                </motion.article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionJourney;
